import React, { ReactNode, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { createAccessRequest, getAccessStatus } from "../services/api/access";
import CenterSpinner from "./CenterSpinner";

interface PublicRouteProps {
  children: ReactNode;
  authenticatedTo?: string;
}

const PublicRoute: React.FC<PublicRouteProps> = ({
  children,
  authenticatedTo = "/home",
}) => {
  const { user, loading } = useAuth();
  const [approved, setApproved] = useState<boolean | null>(null);
  const [checkedUid, setCheckedUid] = useState<string | null>(null);

  useEffect(() => {
    if (!user || user.isGuest) {
      setApproved(null);
      setCheckedUid(null);
      return;
    }
    if (!user.token) {
      // AuthContext publishes the user only after it has obtained and stored a
      // Firebase token. Avoid an unauthenticated admission call before then.
      setApproved(null);
      setCheckedUid(null);
      return;
    }

    let cancelled = false;
    const uid = user.uid;

    const checkAdmission = async (): Promise<void> => {
      setApproved(null);
      try {
        const status = await getAccessStatus();
        const shouldCreateOrClaimRequest =
          status.accessStatus === "not_requested" ||
          (["pending", "approved"].includes(status.accessStatus) &&
            status.request?.identityClaimRequired === true);
        const resolvedStatus = shouldCreateOrClaimRequest
          ? await createAccessRequest()
          : status;
        if (!cancelled) {
          setApproved(
            resolvedStatus.accessStatus === "approved" &&
              resolvedStatus.permissionScope === "full",
          );
        }
      } catch (error) {
        // A failed status check must not optimistically enter Studio.
        console.error("Studio admission status check failed:", error);
        if (!cancelled) {
          setApproved(false);
        }
      } finally {
        if (!cancelled) {
          setCheckedUid(uid);
        }
      }
    };

    void checkAdmission();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (loading) {
    return (
      <>
        <CenterSpinner
          radius={35}
          thickness={10}
          ariaLabel="Loading"
          speedSec={1.4}
          color="#50B786"
          trackColor="rgba(80, 183, 134, 0.3)"
          arcRatio={0.2}
        />
      </>
    );
  }

  if (user?.isGuest) {
    return <Navigate to={authenticatedTo} replace />;
  }

  if (user) {
    if (checkedUid !== user.uid || approved === null) {
      return (
        <CenterSpinner
          radius={35}
          thickness={10}
          ariaLabel="Checking access"
          speedSec={1.4}
          color="#50B786"
          trackColor="rgba(80, 183, 134, 0.3)"
          arcRatio={0.2}
        />
      );
    }

    return <Navigate to={approved ? authenticatedTo : "/access"} replace />;
  }

  return <>{children}</>;
};

export default PublicRoute;
